<template>
  <v-dialog
    id="requestDialog"
    :value="enableDialog"
    persistent
    width="1600"
    @keydown.esc="cancelButtonClickEvent"
    @close="cancelButtonClickEvent"
  >
    <v-card flat>
      <v-toolbar flat>
        <v-toolbar-title>搬入予約</v-toolbar-title>
      </v-toolbar>
      <v-divider />
      <v-card-text style="max-height: 75vh;overflow-y: auto;" class="pt-3">
        <validation-observer ref="carryingRequestValidate">
          <!-- 配車予定日 -->
          <v-row v-if="enableScheduleAllocationDate">
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="forms.scheduled_allocation_date"
                @dateChangedCallback="scheduledAllocationDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :class="{requiredMark: enableScheduleAllocationDate}"
                    :rules="{required: enableScheduleAllocationDate}"
                    name="配車予定日"
                  >
                    <v-text-field
                      v-model="forms.scheduled_allocation_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="配車予定日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
          </v-row>
          <!-- /配車予定日 -->

          <!-- 搬入日, 予定開始時間, 予定終了時間 -->
          <v-row>
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="forms.carrying_date"
                @dateChangedCallback="carryingDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{required: true}"
                    name="搬入日"
                    class="requiredMark"
                  >
                    <v-text-field
                      v-model="forms.carrying_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="搬入日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
            <v-col cols="12" md="2">
              <vue-ctk-date-time-picker
                v-model="forms.schedule_time"
                minute-interval="15"
                color="#0070B3"
                format="HH:mm"
                formatted="HH:mm"
                only-time
                no-value-to-custom-elem
              >
                <v-text-field
                  v-model="forms.schedule_time"
                  append-icon="mdi-clock-time-four-outline"
                  clearable
                  outlined
                  label="予定開始時間"
                  dense
                  persistent-placeholder
                  hide-details="auto"
                  placeholder="--:--"
                  readonly
                />
              </vue-ctk-date-time-picker>
            </v-col>
            <v-col cols="12" md="2">
              <vue-ctk-date-time-picker
                v-model="forms.schedule_end_time"
                minute-interval="15"
                color="#0070B3"
                format="HH:mm"
                formatted="HH:mm"
                only-time
                no-value-to-custom-elem
              >
                <v-text-field
                  v-model="forms.schedule_end_time"
                  append-icon="mdi-clock-time-four-outline"
                  clearable
                  outlined
                  label="予定終了時間"
                  dense
                  persistent-placeholder
                  hide-details="auto"
                  placeholder="--:--"
                  readonly
                />
              </vue-ctk-date-time-picker>
            </v-col>
          </v-row>
          <!-- /搬入日, 予定開始時間, 予定終了時間 -->

          <!-- 運搬担当者, 運搬方法, 車両番号 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transport_user_name`"
                :rules="{ max: 12 }"
                name="運搬担当者"
              >
                <v-combobox
                  v-model="forms.transport_user_name"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  :disabled="!forms.trader_location_code"
                  clearable
                  dense
                  hide-details="auto"
                  item-value="text"
                  label="運搬担当者"
                  outlined
                  persistent-placeholder
                  @click:clear="transportUserClearClickEvent"
                  @change="transportUserChangeEvent"
                >
                  <template v-slot:label>
                    <span>運搬担当者</span>
                    <span class="ml-1">
                      ({{ forms.transport_user_name?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transportation_id`"
                name="運搬方法"
              >
                <v-select
                  v-model="forms.transportation_id"
                  :error-messages="errors"
                  :items="transportations"
                  :disabled="!forms.trader_location_code"
                  dense
                  clearable
                  hide-details="auto"
                  label="運搬方法"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`vehicle_number`"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="forms.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  :disabled="!forms.trader_location_code"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                      ({{ forms.vehicle_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /運搬担当者, 運搬方法, 車両番号 -->

          <!-- 運搬先事業者, 運搬先事業場, 積替保管場所 -->
          <v-row>
            <v-chip v-if="this.forms.trader_name">
              運搬先事業者: {{ this.forms.trader_name }}
            </v-chip>
            <v-chip v-if="this.forms.to_trader_trader_name">
              運搬先事業場: {{ this.forms.to_trader_trader_name }}
            </v-chip>
            <v-chip v-if="this.forms.trader_location_name">
              積替保管場所: {{ this.forms.trader_location_name }}
            </v-chip>
          </v-row>
          <!-- /運搬先事業者, 運搬先事業場, 積替保管場所 -->

          <v-row>
            <v-col>
              <v-expansion-panels
                v-model="panel"
              >
                <!-- ■■■条件を指定して検索（搬入予約） -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="panel-header-color panel-selectable">
                    <strong>
                      <v-icon class="panel-header-color">
                        mdi-magnify
                      </v-icon>
                      &nbsp;条件を指定して検索（搬入予約）
                    </strong>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <validation-observer ref="carryingConditionValidate">
                      <v-form>
                        <v-card flat light>
                          <v-card-text>
                            <!-- 運搬先事業者, 運搬先事業場 -->
                            <infinite-trader-and-location-auto-complete
                              v-if="!conditions"
                              :trader-id="searchForm.cTraderId"
                              :location-id="searchForm.cTraderLocationId"
                              :location-code="searchForm.cTraderLocationCode"
                              :without-invisible="true"
                              classes="search-font"
                              trader-label="運搬先事業者"
                              location-label="運搬先事業場"
                              trader-required
                              location-required
                              trader-md="6"
                              location-md="5"
                              location-auto-select
                              search-type="either"
                              rounded
                              @update:values="cTraderChangedCallback"
                            >
                              <template v-slot:trader-append-outer>
                                <choose-trader-button
                                  :small-button="true"
                                  :button-caption="`運搬先事業者を選択`"
                                  :without-invisible="true"
                                  @choseTraderCallback="choseTraderCallback"
                                />
                              </template>
                              <template v-slot:location-item="slotProps">
                                <v-chip
                                  :title="slotProps.item?.code"
                                  color="#e0ffff"
                                  label
                                  small
                                >
                                  {{ slotProps.item?.code }}
                                </v-chip>
                                &nbsp;{{ slotProps.item.text }}
                                <small class="text--disabled">{{ slotProps.item.address }}</small>
                              </template>
                            </infinite-trader-and-location-auto-complete>
                            <!-- /運搬先事業者, 運搬先事業場 -->

                            <!-- 積替後配車対象, 積替保管場所, 引取搬入番号発番済も表示, 引取搬入番号 -->
                            <v-row dense>
                              <v-col
                                v-if="!conditions"
                                cols="12"
                                md="2"
                                class="text-right"
                              >
                                <v-checkbox
                                  v-model="searchForm.cInHouseTransshipment"
                                  class="mt-2"
                                  dense
                                  hide-details="auto"
                                  label="積替後配車対象"
                                  persistent-hint
                                  @change="traderLocationChangeEvent"
                                />
                              </v-col>
                              <v-col v-if="!conditions" cols="12" md="3">
                                <infinite-tenant-location-auto-complete
                                  :location-id="searchForm.cLocationId"
                                  :location-code="searchForm.cLocationCode"
                                  :disabled="!searchForm.cInHouseTransshipment"
                                  :required="searchForm.cInHouseTransshipment"
                                  :without-invisible="true"
                                  classes="search-font"
                                  label="積替保管場所"
                                  rounded
                                  search-type="transport"
                                  @update:values="cLocationChangedCallback"
                                >
                                  <template v-slot:item="slotProps">
                                    <v-chip
                                      :title="slotProps.item?.code"
                                      color="#e0ffff"
                                      label
                                      small
                                    >
                                      {{ slotProps.item?.code }}
                                    </v-chip>
                                    &nbsp;{{ slotProps.item.text }}
                                    <small class="text--disabled">{{ slotProps.item.address }}</small>
                                  </template>
                                </infinite-tenant-location-auto-complete>
                              </v-col>
                              <v-col cols="12" md="3" class="text-right">
                                <v-checkbox
                                  v-model="searchForm.cWithPublished"
                                  class="mt-2"
                                  dense
                                  label="引取搬入番号発番済も表示"
                                  style="font-size: 70%;"
                                  persistent-hint
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent"
                                ></v-checkbox>
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-text-field
                                  v-model="searchForm.cRequestNo"
                                  class="search-font"
                                  clearable
                                  dense
                                  hide-details="auto"
                                  label="引取搬入番号"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent"
                                  @click:clear="searchForm.cRequestNo = null; searchCarryingButtonClickEvent()"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                            <!-- /積替後配車対象, 積替保管場所, 引取搬入番号発番済も表示, 引取搬入番号 -->

                            <!-- 処分場, 処分事業場 -->
                            <infinite-trader-and-location-auto-complete
                              v-if="!conditions"
                              :trader-id="searchForm.disposalId"
                              :location-id="searchForm.disposalLocationId"
                              :location-code="searchForm.disposalLocationCode"
                              :without-invisible="true"
                              classes="search-font"
                              trader-md="6"
                              location-md="5"
                              trader-label="処分場"
                              location-label="処分事業場"
                              location-auto-select
                              search-type="trader"
                              rounded
                              @update:values="disposalChangedCallback"
                            >
                              <template v-slot:trader-append-outer>
                                <choose-trader-button
                                  small-button
                                  button-caption="処分場を選択"
                                  search-type="trader"
                                  :without-invisible="true"
                                  @choseTraderCallback="choseDisposerCallback"
                                />
                              </template>
                              <template v-slot:location-item="slotProps">
                                <v-chip
                                  :title="slotProps.item?.code"
                                  color="#e0ffff"
                                  label
                                  small
                                >
                                  {{ slotProps.item?.code }}
                                </v-chip>
                                &nbsp;{{ slotProps.item.text }}
                                <small class="text--disabled">{{ slotProps.item.address }}</small>
                              </template>
                            </infinite-trader-and-location-auto-complete>
                            <!-- /処分場, 処分事業場 -->

                            <!-- 回収日（From）, 回収日（To）, 品目 -->
                            <v-row v-if="!conditions">
                              <v-col cols="12" md="3">
                                <cs-date-picker
                                  :date.sync="searchForm.cStartDate"
                                  @dateChangedCallback="cStartDateChangedCallback"
                                >
                                  <template v-slot="{ on, attrs }">
                                    <v-text-field
                                      v-model="searchForm.cStartDate"
                                      v-bind="attrs"
                                      v-on="on"
                                      append-icon="mdi-calendar"
                                      class="search-font"
                                      clearable
                                      dense
                                      hide-details="auto"
                                      label="回収日（From）"
                                      outlined
                                      persistent-hint
                                      persistent-placeholder
                                      placeholder="日付を選択"
                                      readonly
                                      rounded
                                      @click:clear="cStartDateClearEvent"
                                    >
                                      <template v-slot:append-outer>
                                        <div
                                          v-if="$vuetify.breakpoint.mdAndUp"
                                          style="vertical-align: middle"
                                        >
                                          〜
                                        </div>
                                      </template>
                                    </v-text-field>
                                  </template>
                                </cs-date-picker>
                              </v-col>
                              <v-col cols="12" md="3">
                                <cs-date-picker
                                  :date.sync="searchForm.cEndDate"
                                  @dateChangedCallback="cEndDateChangedCallback"
                                >
                                  <template v-slot="{ on, attrs }">
                                    <v-text-field
                                      v-model="searchForm.cEndDate"
                                      v-bind="attrs"
                                      v-on="on"
                                      append-icon="mdi-calendar"
                                      class="search-font"
                                      clearable
                                      dense
                                      hide-details="auto"
                                      label="回収日（To）"
                                      outlined
                                      persistent-hint
                                      persistent-placeholder
                                      placeholder="日付を選択"
                                      readonly
                                      rounded
                                      @click:clear="cEndDateClearCallback"
                                    />
                                  </template>
                                </cs-date-picker>
                              </v-col>
                              <v-col cols="12" md="5">
                                <item-autocomplete
                                  v-model="searchForm.cItemId"
                                  :items="items"
                                  label="品目"
                                  classes="search-font"
                                  clearable
                                  dense
                                  hide-details="auto"
                                  outlined
                                  rounded
                                  multiple
                                  chips
                                  small-chips
                                  deletable-chips
                                  persistent-placeholder
                                  @change="searchCarryingButtonClickEvent"
                                />
                              </v-col>
                            </v-row>
                            <!-- /回収日（From）, 回収日（To）, 品目 -->

                            <!-- マニフェスト区分, DXEマニ番号, 並び替え, 運搬完了済を含む -->
                            <v-row dense>
                              <v-col cols="12" md="3">
                                <v-select
                                  v-model="searchForm.cManifestType"
                                  :items="manifestTypes"
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  clearable
                                  rounded
                                  label="マニフェスト区分"
                                  persistent-placeholder
                                  @change="searchCarryingButtonClickEvent"
                                >
                                </v-select>
                              </v-col>
                              <v-col cols="12" md="2">
                                <v-text-field
                                  v-model="searchForm.cManifestId"
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  label="DXEマニ番号"
                                  clearable
                                  outlined
                                  persistent-hint
                                  persistent-placeholder
                                  rounded
                                  placeholder="カンマ区切りで入力"
                                  hint="例: 1, 2, 3"
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cManifestId = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>
                              <v-col cols="12" md="2">
                                <v-select
                                  v-model="searchForm.cSortType"
                                  :items="sortMembers"
                                  class="search-font"
                                  dense
                                  outlined
                                  clearable
                                  rounded
                                  hide-details="auto"
                                  label="並び替え"
                                  persistent-placeholder
                                  @change="searchCarryingButtonClickEvent"
                                />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-checkbox
                                  v-model="searchForm.cIncludeCompleted"
                                  class="mt-2"
                                  label="運搬完了済を含む"
                                  dense
                                  persistent-hint
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent"
                                />
                              </v-col>
                            </v-row>
                            <!-- /マニフェスト区分, DXEマニ番号, 並び替え, 運搬完了済を含む -->

                            <!-- カスタムラベル -->
                            <v-row
                              v-if="isJwMode"
                              dense
                            >
                              <v-col
                                v-if="customLabel.custom_labels1_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels1"
                                  :disabled="(searchForm.cCustomLabels1Unregistered)"
                                  :label="customLabel.custom_labels1_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels1 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>

                              <v-col
                                v-if="customLabel.custom_labels2_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels2"
                                  :disabled="(searchForm.cCustomLabels2Unregistered)"
                                  :label="customLabel.custom_labels2_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels2 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>

                              <v-col
                                v-if="customLabel.custom_labels3_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels3"
                                  :disabled="(searchForm.cCustomLabels3Unregistered)"
                                  :label="customLabel.custom_labels3_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels3 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>

                              <v-col
                                v-if="customLabel.custom_labels4_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels4"
                                  :disabled="(searchForm.cCustomLabels4Unregistered)"
                                  :label="customLabel.custom_labels4_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels4 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>

                              <v-col
                                v-if="customLabel.custom_labels5_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels5"
                                  :disabled="(searchForm.cCustomLabels5Unregistered)"
                                  :label="customLabel.custom_labels5_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels5 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>

                              <v-col
                                v-if="customLabel.custom_labels6_use"
                                cols="12"
                                md="2"
                              >
                                <v-text-field
                                  v-model="searchForm.cCustomLabels6"
                                  :disabled="(searchForm.cCustomLabels6Unregistered)"
                                  :label="customLabel.custom_labels6_name"
                                  clearable
                                  class="search-font"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  persistent-placeholder
                                  rounded
                                  @blur="searchCarryingButtonClickEvent()"
                                  @click:clear="searchForm.cCustomLabels6 = null; searchCarryingButtonClickEvent()"
                                >
                                </v-text-field>
                              </v-col>
                            </v-row>
                            <!-- /カスタムラベル -->

                            <v-row
                              v-if="isJwMode"
                              dense
                            >
                              <v-col v-if="customLabel.custom_labels1_use" cols="12" md="2" >
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels1Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>

                              <v-col v-if="customLabel.custom_labels2_use" cols="12" md="2">
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels2Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>

                              <v-col v-if="customLabel.custom_labels3_use" cols="12" md="2">
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels3Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>

                              <v-col v-if="customLabel.custom_labels4_use" cols="12" md="2">
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels4Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>

                              <v-col v-if="customLabel.custom_labels5_use" cols="12" md="2">
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels5Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>

                              <v-col v-if="customLabel.custom_labels6_use" cols="12" md="2">
                                <v-checkbox
                                  v-model="searchForm.cCustomLabels6Unregistered"
                                  dense
                                  hide-details="auto"
                                  @change="searchCarryingButtonClickEvent()"
                                >
                                  <template v-slot:label>
                                    <small>未入力データのみ検索</small>
                                  </template>
                                </v-checkbox>
                              </v-col>
                            </v-row>

                          </v-card-text>
                          <v-card-actions>
                            <v-spacer />
                            <v-btn
                              color="primary"
                              @click="searchCarryingButtonClickEvent"
                            >
                              <v-icon>
                                mdi-magnify
                              </v-icon>
                              &nbsp;検索
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-form>
                    </validation-observer>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <!-- /条件を指定して検索（搬入予約） -->

              </v-expansion-panels>
            </v-col>
          </v-row>

          <!-- 紐付けされているマニフェスト一覧 -->
          <v-row v-if="conditions">
            <v-col>
              <!-- 一覧 -->
              <v-card flat class="rounded-card">
                <v-card-subtitle>
                  <v-row>
                    <v-col cols="12" md="4">
                      紐付けされているマニフェスト
                    </v-col>
                    <v-col cols="12" md="8">
                      <custom-pagination
                        :page-number="mapPage"
                        :per-page="mapPerPage"
                        :pagination="mapPagination"
                        :total-visible="totalVisible"
                        @pageChanged="pageMapChangeEvent"
                        @perPageChanged="perPageMapChangeEvent"
                      />
                    </v-col>
                  </v-row>
                </v-card-subtitle>
                <v-card-text>
                  <v-data-table
                    v-model="currentSelectedRequests"
                    :headers="tableHeaders"
                    :items="mapRequests"
                    :items-per-page="-1"
                    dense
                    disable-sort
                    hide-default-footer
                    show-select
                    @toggle-select-all="selectMapAllToggle"
                    @click:row="toggleMapRowSelection"
                  >
                    <!-- 選択チェックボックス -->
                    <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                      <v-simple-checkbox
                        :value="isSelected"
                        @input="select($event)"
                        @click="checkboxMapClickEvent(!isSelected, item)"
                      ></v-simple-checkbox>
                    </template>
                    <!-- /選択チェックボックス -->

                    <!-- マニフェスト番号 -->
                    <template v-slot:[`item.manifest_no`]="{item}">
                      <small>{{ item.manifest_no }}</small>
                    </template>
                    <!-- /マニフェスト番号 -->

                    <!-- DXEマニ番号 -->
                    <template v-slot:[`item.id`]="{item}">
                      <small>{{ item.id }}</small>
                    </template>
                    <!-- /DXEマニ番号 -->

                    <!-- 引取搬入番号 -->
                    <template v-slot:[`item.request_no`]="{item}">
                      <small>{{ item.request_no }}</small>
                    </template>
                    <!-- /引取搬入番号 -->

                    <!-- 回収日 -->
                    <template v-slot:[`item.collected_date`]="{item}">
                      <small>{{ item.collected_date | date_format }}</small>
                    </template>
                    <!-- /回収日 -->

                    <!-- 排出事業者／事業場 -->
                    <template v-slot:[`item.emission`]="{item}">
                      <div v-if="item.emission" :title="item.emission.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 180px;display: inline-block;vertical-align: middle;"
                        >
                          {{ item.emission.name }}
                        </small>
                        <v-tooltip
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              v-if="item.emission.addresses"
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <small
                            v-if="item.emission.addresses"
                          >
                            {{ item.emission.addresses }}
                          </small>
                        </v-tooltip>
                      </div>
                      <div v-if="item.emission_location" :title="item.emission_location.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 180px;display: inline-block;vertical-align: middle;"
                        >{{ item.emission_location.name}}</small>
                        <v-tooltip
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              v-if="item.emission_location.addresses"
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <small
                            v-if="item.emission_location.addresses"
                          >
                            {{ item.emission_location.addresses }}
                          </small>
                        </v-tooltip>
                      </div>
                    </template>
                    <!-- /排出事業者／事業場 -->

                    <!-- 運搬先 -->
                    <template v-slot:[`item.trader`]="{ item }">
                      <div v-if="item.trader" :title="item.trader.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 150px;display: inline-block;vertical-align: middle;"
                        >
                          {{ item.trader.name }}
                        </small>
                        <v-tooltip
                          v-if="item.trader.location"
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <div>
                            <strong>{{ item.trader.location.name }}</strong>
                            <br />
                            <small>
                              {{ item.trader.location.address }}
                            </small>
                          </div>
                        </v-tooltip>
                      </div>
                    </template>
                    <!-- /運搬先 -->

                    <!-- 処分事業場 -->
                    <template v-slot:[`item.disposer_location_name`]="{item}">
                      <small>{{ item.disposer_location_name }}</small>
                    </template>
                    <!-- /処分事業場 -->

                    <template v-slot:[`item.item_name`]="{item}">
                      <small>{{ item.item_name }}</small>
                    </template>


                  </v-data-table>
                </v-card-text>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <custom-pagination
                        :page-number="mapPage"
                        :per-page="mapPerPage"
                        :pagination="mapPagination"
                        :total-visible="totalVisible"
                        @pageChanged="pageMapChangeEvent"
                        @perPageChanged="perPageMapChangeEvent"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <!-- /一覧 -->
            </v-col>
          </v-row>
          <!-- /紐付けされているマニフェスト一覧 -->

          <v-row>
            <v-col>
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <!-- 検索結果一覧 -->
              <v-card flat class="rounded-card">
                <v-card-subtitle>
                  <v-row>
                    <v-col cols="12" md="3">検索結果</v-col>
                    <v-col cols="12" md="9">
                      <custom-pagination
                        :page-number="page"
                        :per-page="perPage"
                        :pagination="pagination"
                        :total-visible="totalVisible"
                        @pageChanged="pageChangeEvent"
                        @perPageChanged="perPageChangeEvent"
                      />
                    </v-col>
                  </v-row>
                </v-card-subtitle>
                <v-card-text>
                  <v-data-table
                    v-model="currentSelectedItems"
                    :headers="tableHeaders"
                    :items="requests"
                    :items-per-page="-1"
                    dense
                    disable-sort
                    hide-default-footer
                    show-select
                    @toggle-select-all="selectAllToggle"
                    @click:row="toggleRowSelection"
                  >

                    <!-- 選択チェックボックス -->
                    <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                      <v-simple-checkbox
                        :value="isSelected"
                        :readonly="item.disabled"
                        :disabled="item.disabled"
                        @input="select($event)"
                        @click="checkboxClickEvent(!isSelected, item)"
                      />
                    </template>
                    <!-- /選択チェックボックス -->

                    <!-- マニフェスト番号 -->
                    <template v-slot:[`item.manifest_no`]="{item}">
                      <small>{{ item.manifest_no }}</small>
                    </template>
                    <!-- /マニフェスト番号 -->

                    <!-- DXEマニ番号 -->
                    <template v-slot:[`item.id`]="{item}">
                      <small>{{ item.id }}</small>
                    </template>
                    <!-- /DXEマニ番号 -->

                    <!-- 引取搬入番号 -->
                    <template v-slot:[`item.request_no`]="{item}">
                      <small>{{ item.request_no }}</small>
                    </template>
                    <!-- /引取搬入番号 -->

                    <!-- 回収日 -->
                    <template v-slot:[`item.collected_date`]="{item}">
                      <small>{{ item.collected_date | date_format }}</small>
                    </template>
                    <!-- /回収日 -->

                    <!-- 排出事業者／事業場 -->
                    <template v-slot:[`item.emission`]="{item}">
                      <div v-if="item.emission" :title="item.emission.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 180px;display: inline-block;vertical-align: middle;"
                        >
                          {{ item.emission.name }}
                        </small>
                        <v-tooltip
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              v-if="item.emission.addresses"
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <small
                            v-if="item.emission.addresses"
                          >
                            {{ item.emission.addresses }}
                          </small>
                        </v-tooltip>
                      </div>
                      <div v-if="item.emission_location" :title="item.emission_location.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 180px;display: inline-block;vertical-align: middle;"
                        >{{ item.emission_location.name}}</small>
                        <v-tooltip
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              v-if="item.emission_location.addresses"
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <small
                            v-if="item.emission_location.addresses"
                          >
                            {{ item.emission_location.addresses }}
                          </small>
                        </v-tooltip>
                      </div>
                    </template>
                    <!-- /排出事業者／事業場 -->

                    <!-- 運搬先 -->
                    <template v-slot:[`item.trader`]="{ item }">
                      <div v-if="item.trader" :title="item.trader.name">
                        <small
                          class="text-ellipsis text-sm-caption"
                          style="max-width: 150px;display: inline-block;vertical-align: middle;"
                        >
                          {{ item.trader.name }}
                        </small>
                        <v-tooltip
                          v-if="item.trader.location"
                          top
                          :open-on-hover="false"
                        >
                          <template #activator="{ on }">
                            <v-btn
                              icon
                              small
                              retain-focus-on-click
                              @click="on.click"
                              @blur="on.blur"
                            >
                              <v-icon small>
                                mdi-map-marker-outline
                              </v-icon>
                            </v-btn>
                          </template>
                          <div>
                            <strong>{{ item.trader.location.name }}</strong>
                            <br />
                            <small>
                              {{ item.trader.location.address }}
                            </small>
                          </div>
                        </v-tooltip>
                      </div>
                    </template>
                    <!-- /運搬先 -->

                    <!-- 処分事業場 -->
                    <template v-slot:[`item.disposer_location_name`]="{item}">
                      <small>{{ item.disposer_location_name }}</small>
                    </template>
                    <!-- /処分事業場 -->

                    <template v-slot:[`item.item_name`]="{item}">
                      <small>{{ item.item_name }}</small>
                    </template>

                  </v-data-table>
                </v-card-text>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <custom-pagination
                        :page-number="page"
                        :per-page="perPage"
                        :pagination="pagination"
                        :total-visible="totalVisible"
                        @pageChanged="pageChangeEvent"
                        @perPageChanged="perPageChangeEvent"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <!-- /検索結果一覧 -->
            </v-col>
          </v-row>

        </validation-observer>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="cancelButtonClickEvent"
        >
          閉じる
        </v-btn>
        <VButtonProcessing
          :disabled="(selectedItems.length <= 0 && !conditions) || !conditionMatch"
          :onclick="registerButtonClickEvent"
          color="primary"
          elevation="5"
        >
          登録
        </VButtonProcessing>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import { mapActions, mapState } from 'vuex'
import ChooseTraderButton from "@/driver/components/ChooseTraderButton"
import VButtonProcessing from '@/components/VButtonProcessing.vue'
import CustomPagination from "@/driver/components/_CustomPagination.vue";
import InfiniteTraderAndLocationAutoComplete from "@/driver/components/InfiniteTraderAndLocationAutoComplete.vue";
import InfiniteTenantLocationAutoComplete from "@/driver/components/InfiniteTenantLocationAutoComplete.vue";
import ItemAutocomplete from "@/driver/components/ItemAutocomplete.vue";

export default {
  name: 'CarryingRequestDialog',
  components: {
    InfiniteTenantLocationAutoComplete,
    InfiniteTraderAndLocationAutoComplete,
    CustomPagination,
    ChooseTraderButton,
    VButtonProcessing,
    ItemAutocomplete
  },
  mixins: [],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 検索条件
     * @type { Object }
     */
    conditions: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      panel: 0,
      mapPage: 1,
      page: 1,
      perPage: 50,
      mapPerPage: 50,
      totalVisible: 7,
      enableScheduleAllocationDate: false,
      forms: {
        order_id: null,
        order_carryin_id: null,
        request_no: null,
        scheduled_allocation_date: null,
        carrying_date: null,
        schedule_time: null,
        schedule_end_time: null,
        transport_user_name: null,
        transport_user_id: null,
        transportation_id: 1,       //車両を初期値設定
        vehicle_number: null,
        trader_id: null,
        trader_name: null,
        to_trader_location_code: null,
        to_trader_trader_name: null,
        trader_location_code: null,
        trader_location_name: null,
      },
      searchForm: {
        searchType: null,
        // 運搬先事業者ID
        cTraderId: null,
        cTraderName: null,
        // 運搬先事業場コード
        cTraderLocationId: null,
        cTraderLocationCode: null,
        // 処分事業者ID
        disposalId: null,
        // 処分事業者名
        disposalName: null,
        // 処分事業場コード
        disposalLocationId: null,
        disposalLocationCode: null,
        // 積替後配車対象
        cInHouseTransshipment: false,
        // 事業場コード
        cLocationId: null,
        cLocationCode: null,
        // マニフェスト区分
        cManifestType: null,
        // 回収日 開始
        cStartDate: null,
        // 回収日 終了
        cEndDate: null,
        // 品目ID
        cItemId: null,
        // 引取搬入番号発行済み
        cWithPublished: false,
        // マニフェストID
        cManifestId: null,
        // 引取搬入番号
        cRequestNo: null,
        // 並び替え
        cSortType: null,
        cCustomLabels1: null,
        cCustomLabels2: null,
        cCustomLabels3: null,
        cCustomLabels4: null,
        cCustomLabels5: null,
        cCustomLabels6: null,
        cCustomLabels1Unregistered: false,
        cCustomLabels2Unregistered: false,
        cCustomLabels3Unregistered: false,
        cCustomLabels4Unregistered: false,
        cCustomLabels5Unregistered: false,
        cCustomLabels6Unregistered: false,
        // 運搬完了済を含む
        cIncludeCompleted: false,
      },
      manifestTypes: [
        { value: 'dxe_form', text: '電子マニフェスト(EDI)' },
        { value: 'other_form', text: '電子マニフェスト(WEB)' },
        { value: 'paper_form', text: '紙マニフェスト' },
        { value: 'transfer_form', text: '電子マニフェスト(取込)' },
      ],
      sortMembers: [
        { value: 'updated', text: '更新日降順' },
        { value: 'collected', text: '回収日昇順' },
      ],
      tableHeaders: [
        { value: 'manifest_no', text: 'マニフェスト番号', width: '9%' },
        { value: 'id', text: 'DXEマニ番号', width: '8%' },
        { value: 'request_no', text: '引取搬入番号', width: '8%' },
        { value: 'collected_date', text: '回収日', width: '7%' },
        { value: 'emission', text: '排出事業者\r\n排出事業場', width: '18%' },
        { value: 'trader', text: '運搬先', width: '15%' },
        { value: 'disposer_location_name', text: '処分事業場', width: '13%' },
        { value: 'item_name', text: '品目', width: '22%' },
      ],
      pagination: {
        current_page: 1,
        total_count: 0
      },
      mapPagination: {
        current_page: 1,
        total_count: 0
      },
      items: [],
      /* 検索結果マニフェスト */
      requests: [],
      /* 紐付けされたマニフェスト */
      mapRequests: [],
      /* 送信用選択情報 */
      selectedItems: [],
      /* 送信用紐付けされたマニフェスト選択情報 */
      selectedMapItems: [],
      /* ページ毎に管理する選択情報 */
      currentSelectedItems: [],
      currentSelectedRequests: [],
      /* ページ毎に管理する選択不可件数 */
      currentDisabledCount: 0,
      /* カスタムラベルマスタ */
      customLabel: null,
      drivers: [],
      transportations: [],
      vehicles: [],
      selectedCTraderIds: [],
      selectedDisposalIds: [],

    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isJwMode: 'use_jw_mode',
    }),
    conditionMatch () {
      return (
        this.forms.trader_id === this.searchForm.cTraderId &&
        this.forms.to_trader_location_code === this.searchForm.cTraderLocationCode &&
        this.forms.trader_location_code === this.searchForm.cLocationCode
      )
    },
  },
  watch: {
    requests : {
      handler: function () {
        this.currentDisabledCount = 0
        this.requests.map((item) => {
          if (item.disabled) {
            if (item.disabled) this.currentDisabledCount += 1
          }
        })
      },
      deep: true
    },
    enableDialog (newValue) {
      this.list_clear()
      this.$nextTick(() => this.$refs.carryingRequestValidate.reset())
      Object.assign(this.$data.forms, this.$options.data().forms)
      Object.assign(this.$data.searchForm, this.$options.data().searchForm)
      if (newValue) {
        if (this.conditions!=null) {
          this.setVariable()
          this.searchManifestMapRequests()
        }
      }
    },
    /**
     * 積替後配車対象 による制御
     * @param newVal
     */
    'searchForm.cInHouseTransshipment': function(newVal) {
      if (!newVal) {
        this.searchForm.cLocationId = null;
        this.searchForm.cLocationCode = null;
      }
    },
    /**
     * 引取搬入番号発番済み による制御
     * @param newVal
     */
    'searchForm.cWithPublished': function(newVal) {
      if (!newVal) {
        this.searchForm.cRequestNo = null
      }
    },
  },
  created() {
    this.fetchItems().then((content) =>{
      this.items = content.items
    })
    this.fetchCustomLabel().then((content) => { this.customLabel = content })
    this.fetchDrivers({without_invisible: true}).then((content) => { this.drivers = content.drivers })
    this.fetchTransportations().then((content) => {
      this.transportations = content.transportations
    })
    this.fetchVehicles({ registered_number: true}).then((content) => { this.vehicles = content.vehicles })
  },
  methods: {
    ...mapActions('DriverManifestRequest', {
      fetchManifestRequest: 'index',
    }),
    ...mapActions('DriverManifestMapRequest', {
      fetchManifestMapRequest: 'index',
    }),
    ...mapActions('DriverMasterSearchItem', {
      fetchItems: 'index'
    }),
    ...mapActions('DriverOrderRequest', {
      createCarryingRequest: 'create'
    }),
    ...mapActions('snackbar', {
      showError: 'showError',
    }),
    ...mapActions('DriverMasterSearchCustomLabel', {
      fetchCustomLabel: 'index',
    }),
    ...mapActions('DriverMasterSearchDriver', {
      fetchDrivers: 'index',
    }),
    ...mapActions('DriverMasterSearchTransportation', {
      fetchTransportations: 'index'
    }),
    ...mapActions('DriverMasterSearchVehicle', {
      fetchVehicles: 'search',
    }),
    /**
     * 運搬先事業者 チェンジコールバック
     * @param trader_id
     * @param location_id
     * @param location_code
     */
    cTraderChangedCallback (trader_id, location_id, location_code) {

      let changed = false;

      if (this.searchForm.cTraderId !== trader_id) {
        changed = true;
      }

      if (this.searchForm.cTraderLocationId !== location_id) {
        changed = true;
      }

      this.searchForm.cTraderId = trader_id;
      this.searchForm.cTraderLocationId = location_id;
      this.searchForm.cTraderLocationCode = location_code;

      if (changed) {
        this.$nextTick(() => {this.searchCarryingButtonClickEvent()})
      }
    },
    /**
     * 運搬先事業者ダイアログ コールバック
     * @param item
     */
    choseTraderCallback (item) {
      this.searchForm.cTraderId = item.id;
      this.searchForm.cTraderLocationId = null;
      this.searchForm.cTraderLocationCode = null;
      this.$nextTick(() => {this.searchCarryingButtonClickEvent()})
    },
    /**
     * 積替保管場所 チェンジコールバック
     * @param location_id
     * @param location_code
     */
    cLocationChangedCallback (location_id, location_code) {
      this.searchForm.cLocationId = location_id;
      this.searchForm.cLocationCode = location_code;
      this.list_clear()
      this.$nextTick(() => {this.searchCarryingButtonClickEvent();})
    },
    disposalChangedCallback (trader_id, location_id, location_code) {

      let changed = false;

      if (this.searchForm.disposalId !== trader_id) {
        changed = true;
      }

      if (this.searchForm.disposalLocationId !== location_id) {
        changed = true;
      }

      this.searchForm.disposalId = trader_id;
      this.searchForm.disposalLocationId = location_id;
      this.searchForm.disposalLocationCode = location_code;

      if (changed) {
        this.$nextTick(() => {this.searchCarryingButtonClickEvent();})
      }

    },
    /**
     *
     * @param item
     */
    choseDisposerCallback (item) {
      this.searchForm.disposalId = item.id;
      this.searchForm.disposalLocationId = null;
      this.searchForm.disposalLocationCode = null;
      this.$nextTick(() => {this.searchCarryingButtonClickEvent();})
    },
    /**
     * 配車予定日変更　コールバック
     */
    scheduledAllocationDateChangedCallback (value) {
      this.forms.scheduled_allocation_date = value;
    },
    /**
     * 搬入日変更　コールバック
     */
    carryingDateChangedCallback (value) {
      this.forms.carrying_date = value;
    },
    /**
     * 回収日（from）変更　コールバック
     */
    cStartDateChangedCallback (value) {
      this.searchForm.cStartDate = value;
      this.searchCarryingButtonClickEvent();
    },
    cStartDateClearEvent () {
      this.searchForm.cStartDate = null;
      this.searchCarryingButtonClickEvent();
    },
    /**
     * 回収日（to）変更　コールバック
     */
    cEndDateChangedCallback (value) {
      this.searchForm.cEndDate = value;
      this.searchCarryingButtonClickEvent();
    },
    cEndDateClearCallback () {
      this.searchForm.cEndDate = null;
      this.searchCarryingButtonClickEvent();
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchCarryingButtonClickEvent () {
      this.$refs.carryingConditionValidate.validate().then((result) => {
        if (result) {
          this.page = 1
          this.searchManifestRequests()
          this.selectedItems = []
          this.currentSelectedItems = []
        }
      })
    },
    /**
     * キャンセルボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.$emit('cancelCallback')
    },
    /**
     * 運搬担当者 チェンジイベント
     */
    transportUserChangeEvent (item) {
      const transportUser = this.drivers.find( (v) => v.text.replace(/\s+/g,'') === item.replace(/\s+/g,''))
      if (transportUser) {
        this.forms.transport_user_id = transportUser.value
      } else {
        this.forms.transport_user_id = null
      }
    },
    /**
     * 運搬担当者クリア クリックイベント
     */
    transportUserClearClickEvent () {
      this.forms.transport_user_id = null
      this.forms.transport_user_name = null
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.carryingRequestValidate.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              let removeItemIds = []
              let requestItems = []
              for (const item of this.selectedMapItems) {
                if (!item.selected) {
                  removeItemIds.push(item.id)
                }
              }
              this.selectedItems.map(item => {
                requestItems.push(item)
              })
              let params = {
                //検索種別
                search_type: 'carrying',
                //オーダーID（編集時のみ）
                order_id: this.forms.order_id,
                // 予定日
                plan_date: this.forms.carrying_date,
                // 予定開始時間
                plan_start_time: this.forms.schedule_time,
                // 予定終了時間
                plan_end_time: this.forms.schedule_end_time,
                /* 運搬担当者 */
                transport_user_id: this.forms.trader_location_code ? this.forms.transport_user_id : null,
                transport_user_name: this.forms.trader_location_code ? this.forms.transport_user_name : null,
                /* 運搬方法　*/
                transportation_id: this.forms.trader_location_code ? this.forms.transportation_id : null,
                /* 運搬車両名 */
                vehicle_number: this.forms.trader_location_code ? this.forms.vehicle_number : null,

                order_carryin_attributes: {
                  //オーダー搬入ID（編集時のみ）
                  id: this.forms.order_carryin_id,
                  // 搬入日
                  carryin_plan_date: this.forms.carrying_date,
                  // 予定開始時間
                  carryin_plan_time: this.forms.schedule_time,
                  // 予定終了時間
                  carryin_plan_end_time: this.forms.schedule_end_time,
                  // 運搬先事業者ID
                  trader_id: this.forms.trader_id,
                  // 運搬先事業場コード
                  to_trader_location_code: this.forms.to_trader_location_code,
                  // 運搬元事業場ID
                  trader_location_code: this.forms.trader_location_code,
                },
                // 検索結果一覧で選択されたマニフェストID
                order_manifests_attributes: requestItems,
                remove_manifest_ids: removeItemIds,
              }
              this.createCarryingRequest({params: params}).then(() => {
                this.$emit('registerCallback')
              })
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve()
        }, 1000)
      })
    },
    /**
     * 紐づくマニフェストページ チェンジイベント
     */
    pageMapChangeEvent (page) {
      this.mapPage = page;
      this.searchManifestMapRequests()
    },
    perPageMapChangeEvent (perPage) {
      this.mapPerPage = perPage;
      this.mapPage = 1;
      this.searchManifestMapRequests();
    },
    /**
     * 検索結果ページ チェンジイベント
     */
    pageChangeEvent (page) {
      this.page = page;
      this.searchManifestRequests()
    },
    perPageChangeEvent (perPage) {
      this.perPage = perPage;
      this.page = 1;
      this.searchManifestRequests()
    },
    /**
     * 検索処理実行
     */
    searchManifestRequests () {
      let params = this.buildParam()
      this.forms.trader_id = this.searchForm.cTraderId
      this.forms.to_trader_location_code =this.searchForm.cTraderLocationCode
      this.forms.trader_location_code = this.searchForm.cLocationCode

      this.fetchManifestRequest(params).then((content) => {
        this.requests = content.manifests
        this.pagination = content.pagination
        // 一覧のチェック状態を設定
        this.currentSelectedItems = []
        for (const manifest of this.selectedItems) {
          let target = this.requests.find((v) => v.id === manifest.id)
          if (target) {
            target.selected = true
            this.currentSelectedItems.push(target)
          }
        }
      })
    },
    /**
     * 編集時 紐付けされたマニフェストを検索
     */
    searchManifestMapRequests () {
      let params = {
        search_type: 'carrying',
        order_id: this.forms.order_id,
        trader_id: this.forms.trader_id,
        to_trader_location_code: this.forms.to_trader_location_code,
        transport_id: null,
        trader_location_code: this.forms.trader_location_code,
        page: this.mapPage
      }
      this.fetchManifestMapRequest(params).then((content) => {
        this.mapRequests = content.manifests
        this.mapPagination = content.pagination
        this.currentSelectedRequests = []

        // 一覧情報を走査
        for (let manifest of this.mapRequests) {
          let target = this.selectedMapItems.find((v) => v.id === manifest.id)
          if (target) {
            if (target.selected) {
              this.currentSelectedRequests.push(target)
            }
          } else {
            manifest.selected = true
            this.currentSelectedRequests.push(manifest)
            this.selectedMapItems.push(manifest)
          }
        }
      })
    },
    /**
     * 検索パラメータの生成
     */
    buildParam () {
      return {
        search_type: 'carrying',
        c_trader_id: this.searchForm.cTraderId,
        c_trader_location_code: this.searchForm.cTraderLocationCode,
        c_location_code: this.searchForm.cLocationCode,
        c_in_house_transshipment: this.searchForm.cInHouseTransshipment,
        c_manifest_type: this.searchForm.cManifestType,
        c_sort_type: this.searchForm.cSortType,
        c_start_date: this.searchForm.cStartDate,
        c_end_date: this.searchForm.cEndDate,
        c_item_id: this.searchForm.cItemId,
        c_with_published: this.searchForm.cWithPublished,
        c_request_no: this.searchForm.cRequestNo,
        c_manifest_id: this.searchForm.cManifestId,
        c_disposal_id: this.searchForm.disposalId,
        c_disposal_location_code: this.searchForm.disposalLocationCode,
        c_custom_labels_1: this.searchForm.cCustomLabels1,
        c_custom_labels_2: this.searchForm.cCustomLabels2,
        c_custom_labels_3: this.searchForm.cCustomLabels3,
        c_custom_labels_4: this.searchForm.cCustomLabels4,
        c_custom_labels_5: this.searchForm.cCustomLabels5,
        c_custom_labels_6: this.searchForm.cCustomLabels6,
        c_custom_labels_1_unregistered: this.searchForm.cCustomLabels1Unregistered,
        c_custom_labels_2_unregistered: this.searchForm.cCustomLabels2Unregistered,
        c_custom_labels_3_unregistered: this.searchForm.cCustomLabels3Unregistered,
        c_custom_labels_4_unregistered: this.searchForm.cCustomLabels4Unregistered,
        c_custom_labels_5_unregistered: this.searchForm.cCustomLabels5Unregistered,
        c_custom_labels_6_unregistered: this.searchForm.cCustomLabels6Unregistered,
        c_include_completed: this.searchForm.cIncludeCompleted,
        limit: this.perPage,
        page: this.page
      }
    },
    /**
     * 検索結果一覧ヘッダーチェックボックス クリックイベント
     * @param props
     */
    selectAllToggle (props) {
      if (this.currentSelectedItems.length !== (this.requests.length - this.currentDisabledCount)) {
        props.items.forEach((item) => {
          if (!item.disabled) {
            let currentSelectedItem = this.currentSelectedItems.find(v => v.id === item.id)
            if (!currentSelectedItem) {
              this.currentSelectedItems.push(item)
            }
            let selectedItem = this.selectedItems.find(v => v.id === item.id)
            if (!selectedItem) {
              this.selectedItems.push({id: item.id, manifest_id: item.id, section_no: item.section_no,trader_id: item.trader.id, trader_location_code: item.trader.location.code})
            }
          }
        })
      } else {
        props.items.forEach((item) => {
          this.currentSelectedItems = this.currentSelectedItems.filter(v => v.id !== item.id)
          this.selectedItems = this.selectedItems.filter(v => v.id !== item.id)
        })
      }
    },
    /**
     * 紐付けマニフェスト一覧 行選択トグル
     */
    toggleMapRowSelection(item) {
      const index = this.currentSelectedRequests.findIndex(row => row === item);
      if (index > -1) {
        // 既に選択されている場合は削除
        this.currentSelectedRequests.splice(index, 1);
        // selectedMapItemsの選択状態も更新
        let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id);
        if (selectedMapItem) {
          selectedMapItem.selected = false;
        }
      } else {
        // 選択されていない場合は追加
        this.currentSelectedRequests.push(item);
        // selectedMapItemsの選択状態も更新
        let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id);
        if (selectedMapItem) {
          selectedMapItem.selected = true;
        }
      }
    },
    /**
     * 検索結果一覧 行選択トグル
     */
    toggleRowSelection(item) {
      // disabledな行は選択不可
      if (item.disabled) {
        return;
      }

      const index = this.currentSelectedItems.findIndex(row => row === item);
      if (index > -1) {
        // 既に選択されている場合は削除
        this.currentSelectedItems.splice(index, 1);
        // selectedItemsからも削除
        this.selectedItems = this.selectedItems.filter(v => v.id !== item.id);
      } else {
        // 選択されていない場合は追加
        this.currentSelectedItems.push(item);
        this.selectedItems.push({id: item.id, manifest_id: item.id, section_no: item.section_no, trader_id: item.trader.id, trader_location_code: item.trader.location.code});
      }
    },
    /**
     * 検索結果 チェックボックス クリックイベント
     * @param value { Boolean } チェックホックスの選択状態
     * @param item { Object } 選択行の詳細情報
     */
    checkboxClickEvent (value, item) {
      if (item.disabled) {
        return false
      }
      if (value) {
        this.currentSelectedItems.push(item)
        this.selectedItems.push({id: item.id, manifest_id: item.id, section_no: item.section_no,trader_id: item.trader.id, trader_location_code: item.trader.location.code})
      } else {
        this.currentSelectedItems = this.currentSelectedItems.filter(v => v.id !== item.id)
        this.selectedItems = this.selectedItems.filter(v => v.id !== item.id)
      }
    },
    /**
     * 紐付けマニフェストチェックボックス クリックイベント 選択したものを保持する
     */
    checkboxMapClickEvent (value, item) {
      // チェックON
      if (value) {
        this.currentSelectedRequests.push(item)
        let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id)
        if (selectedMapItem) {
          selectedMapItem.selected = true
        }
      } else {
        this.currentSelectedRequests = this.currentSelectedRequests.filter(v => v.id !== item.id)
        let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id)
        if (selectedMapItem) {
          selectedMapItem.selected = false
        }
      }
    },
    /**
     * 紐付けマニフェスト一覧ヘッダーチェックボックス制御
     */
    selectMapAllToggle (props) {
      let items = props.items
      let isSelected = props.value
      if (isSelected) {
        for (let item of items) {
          let currentSelectedRequest = this.currentSelectedRequests.find(v => v.id === item.id)
          if (!currentSelectedRequest) {
            this.currentSelectedRequests.push(item)
          }
          let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id)
          if (selectedMapItem) {
            selectedMapItem.selected = true
          }
        }
      } else {
        items.forEach((item) => {
          this.currentSelectedRequests = this.currentSelectedRequests.filter(v => v.id !== item.id)
          let selectedMapItem = this.selectedMapItems.find(v => v.id === item.id)
          if (selectedMapItem) {
            selectedMapItem.selected = false
          }
        })
      }
    },
    /**
     * 初期設定
     */
    setVariable () {
      // オーダーID（編集時のみ）
      this.forms.order_id = this.conditions.order_id
      // オーダー搬入ID（編集時のみ）
      this.forms.order_carryin_id = this.conditions.order_carryin_id
      // 搬入日
      this.forms.carrying_date = this.conditions.carryin_plan_date
      // 予定開始時間
      this.forms.schedule_time = this.conditions.carryin_plan_time
      // 予定終了時間
      this.forms.schedule_end_time = this.conditions.carryin_plan_end_time
      // 運搬担当者ID
      this.forms.transport_user_id = this.conditions.transport_user_id
      // 運搬担当者名
      this.forms.transport_user_name = this.conditions.transport_user_name
      // 運搬方法ID
      this.forms.transportation_id = this.conditions.transportation_id
      // 車両番号
      this.forms.vehicle_number = this.conditions.vehicle_number
      // 運搬先事業者ID
      this.forms.trader_id = this.conditions.trader_id
      // 運搬先名
      this.forms.trader_name = this.conditions.trader_name
      // 運搬先事業場コード
      this.forms.to_trader_location_code = this.conditions.to_trader_location_code
      // 運搬先事業場名
      this.forms.to_trader_trader_name = this.conditions.to_trader_location_name
      // 事業場（積替保管場所）
      this.forms.trader_location_code = this.conditions.trader_location_code
      // 事業場名（積替保管場所）
      this.forms.trader_location_name = this.conditions.trader_location_name

      // 事業場（積替保管場所）
      this.searchForm.cLocationCode = this.conditions.trader_location_code
      // 運搬先事業者ID
      this.searchForm.cTraderId = this.conditions.trader_id
      // 運搬先事業場コード
      this.searchForm.cTraderLocationCode = this.conditions.to_trader_location_code

    },
    /**
     * 一覧クリア処理
     */
    list_clear() {
      this.requests = []
      this.mapRequests = []
      this.currentSelectedItems = []
      this.currentSelectedRequests = []
      this.selectedItems = []
      this.selectedMapItems = []
      this.page = 1
      this.mapPage = 1
      this.mapPerPage = 15;
      this.perPage = 15;
      this.currentDisabledCount = 0
      this.pagination = {current_page: 1, total_count: 0}
      this.mapPagination = {current_page: 1, total_count: 0}
    },
    /**
     * 運搬先事業場 チェンジイベント
     */
    traderLocationChangeEvent() {
      this.list_clear()
    },
  }
}

</script>
